import { useEffect, useRef, useState } from 'react';
import { Download, Eraser } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Input';
import { Slider } from '@/components/ui/Slider';
import { downloadCanvas } from '@/lib/utils';
import { toast } from 'sonner';

export function SignaturePad() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [color, setColor] = useState('#0a0a0b');
  const [width, setWidth] = useState(3);
  const drawing = useRef(false);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  function pos(e: React.PointerEvent<HTMLCanvasElement>) {
    const c = canvasRef.current!;
    const rect = c.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function down(e: React.PointerEvent<HTMLCanvasElement>) {
    drawing.current = true;
    const ctx = canvasRef.current!.getContext('2d')!;
    const p = pos(e);
    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  }
  function move(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return;
    const ctx = canvasRef.current!.getContext('2d')!;
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  }
  function up() {
    drawing.current = false;
  }

  function clear() {
    const c = canvasRef.current!;
    const ctx = c.getContext('2d')!;
    ctx.clearRect(0, 0, c.width, c.height);
  }

  return (
    <div>
      <ToolHeader
        toolId="signature-pad"
        title="Signature pad"
        description="Sign with mouse, trackpad, or pen. Export as transparent PNG."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:order-2 space-y-3">
          <Field label="Pen color">
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="!w-full !h-10"
            />
          </Field>
          <Field label="Stroke width" hint={`${width}px`}>
            <Slider value={width} onChange={setWidth} min={1} max={10} showValue={false} />
          </Field>
          <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border">
            <Button variant="secondary" size="sm" onClick={clear}>
              <Eraser size={11} />
              Clear
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                if (canvasRef.current) {
                  downloadCanvas(canvasRef.current, 'signature.png');
                  toast.success('Saved');
                }
              }}
            >
              <Download size={11} />
              Save PNG
            </Button>
          </div>
        </Card>
        <Card className="lg:col-span-2 lg:order-1 !p-0 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
            Sign here
          </div>
          <canvas
            ref={canvasRef}
            onPointerDown={down}
            onPointerMove={move}
            onPointerUp={up}
            onPointerLeave={up}
            className="w-full h-[400px] cursor-crosshair touch-none bg-bg-elev"
          />
        </Card>
      </div>
    </div>
  );
}

export default SignaturePad;
